/**
 * Enhanced Service Price Calculator - FIXED ACCOUNTING STRUCTURE
 * 
 * This module calculates service prices based only on provided values:
 * - Never assumes defaults for critical values
 * - Requires actual input for all calculation components
 * - Creates clear error messages when required values are missing
 * - FIXED: Proper accounting structure with correct profit calculation
 */

const calculateServicePrice = (service, products, settings, stylist = null) => {
  // Validate required service inputs
  if (!service || !service.time) {
    return { error: 'Missing service information' };
  }
  
  // Validate settings - these must be explicitly provided
  if (!settings) {
    return { error: 'Missing business settings' };
  }
  
  // Check for essential settings
  const missingSettings = [];
  
  if (!settings.overhead_per_minute) {
    missingSettings.push('overhead_per_minute');
  }
  
  if (!settings.owner_profit_per_minute) {
    missingSettings.push('owner_profit_per_minute');
  }
  
  // Support staff is allowed to be zero, but should be calculated if missing
  
  if (missingSettings.length > 0) {
    return { 
      error: `Missing required business settings: ${missingSettings.join(', ')}. Please configure your business settings.` 
    };
  }
  
  // Extract basic values
  const serviceTime = parseFloat(service.time) || 0;
  
  // Only use stylist rate if a stylist is provided
  let stylistHourlyRate = 0;
  
  if (stylist) {
    // Use effective hourly rate if available, else use hourly_pay
    if (stylist.effective_hourly_rate && parseFloat(stylist.effective_hourly_rate) > 0) {
      stylistHourlyRate = parseFloat(stylist.effective_hourly_rate);
    } else if (stylist.hourly_pay && parseFloat(stylist.hourly_pay) > 0) {
      stylistHourlyRate = parseFloat(stylist.hourly_pay);
    } else {
      return { 
        error: `Selected stylist (${stylist.name || stylist.id}) has no valid hourly rate.` 
      };
    }
  } else {
    // No default - if no stylist is selected, stylist cost is zero
    stylistHourlyRate = 0;
  }
  
  const stylistRatePerMinute = stylistHourlyRate / 60;
  
  // Get per-minute rates from settings - never use defaults
  const overheadPerMinute = parseFloat(settings.overhead_per_minute) || 0;
  const ownerProfitPerMinute = parseFloat(settings.owner_profit_per_minute) || 0;
  const supportStaffPerMinute = parseFloat(settings.support_staff_per_minute) || 0;
  
  // Log the values being used
  console.log('Service price calculation using:');
  console.log('- Service time:', serviceTime, 'minutes');
  console.log('- Stylist rate:', stylistHourlyRate, 'per hour');
  console.log('- Overhead per minute:', overheadPerMinute);
  console.log('- Owner profit per minute:', ownerProfitPerMinute);
  console.log('- Support staff per minute:', supportStaffPerMinute);
  
  // Calculate product costs - only use selected products
  let productCost = 0;
  if (products && products.length > 0) {
    products.forEach(item => {
      // Handle different product data structures
      if (item.product) {
        // Structure: { product: { price, qty }, usage }
        const price = parseFloat(item.product.price) || 0;
        const packQty = parseFloat(item.product.qty) || 1;
        const usage = parseFloat(item.usage) || 0;
        
        // Calculate cost: (price ÷ pack qty) × usage
        const itemCost = (price / packQty) * usage;
        productCost += itemCost;
      } else if (item.Price_Inc_Vat !== undefined) {
        // Structure: { Pack_Qty, Price_Inc_Vat, Usage }
        const price = parseFloat(item.Price_Inc_Vat) || 0;
        const packQty = parseFloat(item.Pack_Qty) || 1;
        const usage = parseFloat(item.Usage) || 0;
        
        const itemCost = (price / packQty) * usage;
        productCost += itemCost;
      }
    });
  }
  
  // Round product cost to 2 decimals
  productCost = parseFloat(productCost.toFixed(2));
  console.log('- Product cost:', productCost);
  
  // Calculate time-based costs
  const stylistCost = stylistRatePerMinute * serviceTime;
  console.log('- Stylist cost:', stylistCost.toFixed(2));
  
  // Calculate individual components for the breakdown
  const overheadCost = overheadPerMinute * serviceTime;
  const supportStaffCost = supportStaffPerMinute * serviceTime;
  const ownerProfitCost = ownerProfitPerMinute * serviceTime;
  
  console.log('- Overhead cost:', overheadCost.toFixed(2));
  console.log('- Support staff cost:', supportStaffCost.toFixed(2));
  console.log('- Owner profit cost:', ownerProfitCost.toFixed(2));
  
  // FIXED: Calculate OPERATING COSTS only (no owner profit yet)
  const operatingCosts = overheadCost + supportStaffCost + stylistCost + productCost;
  console.log('- Operating costs (no owner profit):', operatingCosts.toFixed(2));
  
  // Apply VAT to operating costs if registered (BEFORE adding owner profit)
  const isVatRegistered = 
    settings.is_vat_registered === true || 
    settings.is_vat_registered === 'y' || 
    settings.is_vat_registered === 1 ||
    settings.vat_registered === true;
    
  const vatRate = parseFloat(settings.vat_rate || settings.default_vat_rate) || 0;
  
  let vatAmount = 0;
  let operatingCostsWithVat = operatingCosts;
  if (isVatRegistered && vatRate > 0) {
    vatAmount = operatingCosts * (vatRate / 100);
    operatingCostsWithVat += vatAmount;
    console.log('- VAT applied to operating costs:', vatAmount.toFixed(2), 'at rate', vatRate + '%');
  }
  
  // Round operating costs with VAT to 2 decimals
  operatingCostsWithVat = parseFloat(operatingCostsWithVat.toFixed(2));
  console.log('- Operating costs with VAT:', operatingCostsWithVat);
  
  // FIXED: Add owner profit AFTER operating costs to get suggested RRP
  const suggestedPrice = operatingCostsWithVat + ownerProfitCost;
  console.log('- Suggested price (operating + owner profit):', suggestedPrice.toFixed(2));
  
  // Calculate realistic profit margin (owner profit as % of final price)
  const profitMarginPercentage = suggestedPrice > 0 ? (ownerProfitCost / suggestedPrice) * 100 : 0;
  console.log('- Actual profit margin:', profitMarginPercentage.toFixed(1) + '%');
  
  // Return complete calculation results with FIXED structure
  return {
    // Detailed breakdown
    breakdown: {
      product_cost: productCost,
      stylist_cost: parseFloat(stylistCost.toFixed(2)),
      overhead_cost: parseFloat(overheadCost.toFixed(2)),
      support_staff_cost: parseFloat(supportStaffCost.toFixed(2)),
      owner_profit: parseFloat(ownerProfitCost.toFixed(2)),
      total_cost: operatingCostsWithVat, // Operating costs only (breakeven)
      vat_applied: isVatRegistered,
      vat_rate: vatRate,
      vat_amount: parseFloat(vatAmount.toFixed(2))
    },
    
    // Price tiers - FIXED
    prices: {
      breakeven: operatingCostsWithVat,  // Just operating costs
      level_1: parseFloat(suggestedPrice.toFixed(2)),  // Operating + owner profit
      level_1_with_vat: parseFloat(suggestedPrice.toFixed(2)), // VAT already included
      level_2: parseFloat(suggestedPrice.toFixed(2)),  // Same as level 1 for now
      level_2_with_vat: parseFloat(suggestedPrice.toFixed(2)), // VAT already included
    },
    
    // For compatibility with existing code
    recommended_price: parseFloat(suggestedPrice.toFixed(2)),
    price_with_vat: parseFloat(suggestedPrice.toFixed(2)), // VAT already included if applicable
    
    // Settings used in calculation (for reference)
    settings_used: {
      service_time: serviceTime,
      stylist_rate: stylistHourlyRate,
      stylist_id: stylist ? stylist.id : null,
      overhead_per_minute: overheadPerMinute,
      support_staff_per_minute: supportStaffPerMinute,
      owner_profit_per_minute: ownerProfitPerMinute,
      profit_margin_percentage: parseFloat(profitMarginPercentage.toFixed(2)),
      is_vat_registered: isVatRegistered
    }
  };
};

// Export the calculator function
export { calculateServicePrice };

// Default export for backward compatibility
export default calculateServicePrice;